import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import { MAX_VERSIONS_PER_CHANGELOG } from "@productlane/lib/constants"
import { MAIN_PORTAL_INSTANCE_ID } from "@productlane/lib/src/portal-instance"
import { normalizeChangelogSlug } from "@productlane/lib/src/portal-slugs"
import { validateLanguageCode } from "@productlane/lib/src/translations/validate-language"
import {
	CHANGELOG_SAVE_CONFLICT,
	nextVersionNumber,
	shouldMergeIntoHead,
	versionsToPrune,
} from "@productlane/lib/src/versions"

import type { MutatorTx, Schema } from "../schema"
import type {
	CreateChangelogArgs,
	CreateChangelogVersionArgs,
	SaveChangelogContentArgs,
} from "../types"
import type { DeleteID, UpdateValue } from "@rocicorp/zero"

async function applyVersionSnapshot(
	tx: MutatorTx,
	args: CreateChangelogVersionArgs,
	authData: ZeroJwtPayload,
) {
	const versions = await tx.query.changelogVersions
		.where("changelogId", "=", args.changelogId)
		.where("language", "=", args.language)
		.run()

	const source = args.createdBySource ?? "USER"
	const userId = source === "USER" ? authData.userId : null
	const now = Date.now()
	const head = [...versions].sort((a, b) => b.createdAt - a.createdAt)[0]

	if (
		shouldMergeIntoHead({
			head,
			source,
			userId,
			now,
			forceNewVersion: args.forceNewVersion,
		})
	) {
		await tx.mutate.changelogVersions.update({
			id: head!.id,
			createdAt: now,
			title: args.title,
			notes: args.notes,
			imageUrl: args.imageUrl ?? null,
			coverVideoUrl: args.coverVideoUrl ?? null,
		})
		return
	}

	for (const stale of versionsToPrune(versions, MAX_VERSIONS_PER_CHANGELOG)) {
		await tx.mutate.changelogVersions.delete({ id: stale.id })
	}

	await tx.mutate.changelogVersions.insert({
		id: args.id,
		changelogId: args.changelogId,
		createdAt: now,
		version: nextVersionNumber(versions),
		title: args.title,
		notes: args.notes,
		imageUrl: args.imageUrl ?? null,
		coverVideoUrl: args.coverVideoUrl ?? null,
		language: args.language,
		createdBySource: source,
		createdById: userId,
	})
}

// The hash describes one image, so a new cover drops it until the server
// hashes the new file; an unchanged cover keeps its hash across autosaves.
export function keptCoverBlurhash(
	row: { imageUrl?: string | null; imageBlurhash?: string | null } | undefined,
	imageUrl: string | null,
): string | null {
	if (!row || !imageUrl || row.imageUrl !== imageUrl) return null
	return row.imageBlurhash ?? null
}

export const changelogMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateChangelogArgs
			ctx: ZeroJwtPayload
		}) => {
			// Convert dateIso string to timestamp
			const dateTimestamp = new Date(args.dateIso).getTime()

			await tx.mutate.changelogs.insert({
				id: args.id,
				date: dateTimestamp,
				createdAt: dateTimestamp,
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				published: false,
				title: "",
				notes: { type: "doc", content: [] },
				archived: false,
				changelogStatus: null,
				portalInstanceId: args.portalInstanceId ?? null,
				portalInstanceIds: [args.portalInstanceId ?? MAIN_PORTAL_INSTANCE_ID],
			})
		},
	),
	update: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateValue<Schema["tables"]["changelogs"]>
		}) => {
			await tx.mutate.changelogs.update({ updatedAt: Date.now(), ...args })
		},
	),
	// A deliberate slug edit. The server keeps the old slug as a redirect; here
	// the synced rows are enough to reject a slug another entry already holds.
	setSlug: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { id: string; slug: string }
			ctx: ZeroJwtPayload
		}) => {
			const normalized = normalizeChangelogSlug(args.slug)
			if (!normalized.ok) throw new Error(normalized.message)

			const holders = await tx.query.changelogs
				.where("workspaceId", "=", authData.workspaceId)
				.where("slug", "=", normalized.value)
				.where("isDeleted", "=", false)
				.run()
			const holder = holders.find((c) => c.id !== args.id)
			if (holder) {
				throw new Error(
					`"/changelog/${normalized.value}" is used by "${holder.title || "Untitled"}"`,
				)
			}

			await tx.mutate.changelogs.update({
				id: args.id,
				slug: normalized.value,
				updatedAt: Date.now(),
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["changelogs"]>
		}) => {
			await tx.mutate.changelogs.update({
				id: args.id,
				isDeleted: true,
			})
		},
	),
	// Atomic editor save: translation row, base row (default language), and
	// version snapshot in one transaction. `expectedVersion` is a compare-and-set
	// against the base row; a mismatch means someone saved since this editor
	// loaded, and the whole save is rejected instead of overwriting their work.
	saveContent: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: SaveChangelogContentArgs
			ctx: ZeroJwtPayload
		}) => {
			validateLanguageCode(args.language)

			const [changelog] = await tx.query.changelogs
				.where("id", "=", args.changelogId)
				.run()

			if (!changelog) {
				throw new Error("Changelog not found")
			}

			if (
				args.expectedVersion !== null &&
				changelog.version !== args.expectedVersion
			) {
				throw new Error(CHANGELOG_SAVE_CONFLICT)
			}

			const imageUrl = args.imageUrl ?? null
			const [translation] = await tx.query.changelogTranslations
				.where("id", "=", args.translationId)
				.run()
			const coverVideoUrl =
				args.coverVideoUrl === undefined
					? (translation?.coverVideoUrl ?? null)
					: args.coverVideoUrl

			await tx.mutate.changelogTranslations.upsert({
				id: args.translationId,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				workspaceId: authData.workspaceId,
				language: args.language,
				changelogId: args.changelogId,
				title: args.title,
				notes: args.notes,
				imageUrl,
				imageBlurhash: keptCoverBlurhash(translation, imageUrl),
				coverVideoUrl,
			})

			if (args.updateBaseRow) {
				await tx.mutate.changelogs.update({
					id: args.changelogId,
					title: args.title,
					notes: args.notes,
					imageUrl,
					imageBlurhash: keptCoverBlurhash(changelog, imageUrl),
					...(args.coverVideoUrl === undefined
						? {}
						: { coverVideoUrl: args.coverVideoUrl }),
					version: changelog.version + 1,
					updatedAt: Date.now(),
				})
			} else {
				// A translation edit is still an edit of the entry, so the base row's
				// updatedAt has to move for "Edited x ago" to be true.
				await tx.mutate.changelogs.update({
					id: args.changelogId,
					updatedAt: Date.now(),
				})
			}

			if (args.snapshot) {
				await applyVersionSnapshot(
					tx,
					{
						id: args.snapshot.id,
						changelogId: args.changelogId,
						title: args.title,
						notes: args.notes,
						imageUrl: args.imageUrl ?? null,
						coverVideoUrl,
						language: args.language,
						createdBySource: args.snapshot.createdBySource,
						forceNewVersion: args.snapshot.forceNewVersion,
					},
					authData,
				)
			}
		},
	),
	createVersion: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateChangelogVersionArgs
			ctx: ZeroJwtPayload
		}) => {
			// Get the current changelog to validate it exists
			const [changelog] = await tx.query.changelogs
				.where("id", "=", args.changelogId)
				.run()

			if (!changelog) {
				throw new Error("Changelog not found")
			}

			await applyVersionSnapshot(tx, args, authData)
		},
	),
	deleteVersionsByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			// Get all changelogs for this workspace
			const changelogs = await tx.query.changelogs
				.where("workspaceId", "=", workspaceId)
				.related("versions", (q) => q.where("language", "=", language))
				.run()

			await Promise.all(
				changelogs.map((changelog) =>
					Promise.all(
						changelog.versions.map((version) =>
							tx.mutate.changelogVersions.delete({ id: version.id }),
						),
					),
				),
			)
		},
	),
}
